import moment from "moment";
import PunctualCourseRepository, { TeachrCourseSessionInfo } from "../../Repositories/PunctualCourseRepository";
import { getActivePrestationBrandConfig } from "../../config/environment.config";
import { SessionRow, formatSessionSlot, sessionEnd, sessionRangesOverlap, sessionStart } from "./SessionList";
import { describeConflictWithOtherCourse } from "./overlapMessages";

/**
 * Détection, avant l'envoi, d'un chevauchement entre les séances saisies et
 * les autres cours du prof. Le serveur reste le garant : il refuse la
 * création ou la modification avec les codes de overlapMessages.ts.
 */
export interface ExternalOverlap {
    /** Numéro de la séance saisie en conflit, à partir de 1, dans l'ordre d'affichage. */
    index: number;
    slot: string;
    existingSlot: string;
    studentName: string;
}

/**
 * Première séance saisie (non verrouillée, complète) qui chevauche une
 * séance d'un autre cours du prof.
 */
export const findExternalOverlap = (rows: SessionRow[], existing: TeachrCourseSessionInfo[]): ExternalOverlap | null => {
    for (let i = 0; i < rows.length; i++) {
        if (rows[i].locked) continue;
        const start = sessionStart(rows[i]);
        const end = sessionEnd(rows[i]);
        if (!start || !end) continue;
        for (const session of existing) {
            const otherStart = moment(session.date);
            const otherEnd = moment(session.endDate);
            if (sessionRangesOverlap(start, end, otherStart, otherEnd)) {
                return {
                    index: i + 1,
                    slot: formatSessionSlot(start, end),
                    existingSlot: formatSessionSlot(otherStart, otherEnd),
                    studentName: session.studentName,
                };
            }
        }
    }
    return null;
};

export const describeExternalOverlap = (overlap: ExternalOverlap, sessionsCount: number): string => {
    const which = sessionsCount > 1
        ? `La séance n°${overlap.index} (${overlap.slot})`
        : `Ce ${getActivePrestationBrandConfig()} (${overlap.slot})`;
    return describeConflictWithOtherCourse(overlap.slot, overlap.existingSlot, overlap.studentName, which);
};

/**
 * Interroge les séances actives du prof sur la période couverte par les
 * lignes saisies et renvoie le premier chevauchement. Une erreur d'appel ne
 * bloque pas la saisie : le serveur fera le contrôle définitif à l'envoi.
 */
export const fetchExternalOverlap = async (
    teachrId: number | undefined,
    rows: SessionRow[],
    excludeCourseId?: number
): Promise<ExternalOverlap | null> => {
    const candidates = rows.filter((row) => !row.locked && sessionStart(row) !== null);
    if (!teachrId || candidates.length === 0) return null;
    const starts = candidates.map((row) => sessionStart(row)!);
    const ends = candidates.map((row) => sessionEnd(row)!);
    const from = moment.min(starts).clone().subtract(1, "day").format("YYYY-MM-DD");
    const to = moment.max(ends).clone().add(1, "day").format("YYYY-MM-DD");
    try {
        const { sessions } = await PunctualCourseRepository.getTeachrCourseSessions(teachrId, from, to, excludeCourseId);
        return findExternalOverlap(rows, Array.isArray(sessions) ? sessions : []);
    } catch (e) {
        console.warn("Séances du prof indisponibles, le contrôle se fera à l'envoi", e);
        return null;
    }
};

/** Ligne de séance construite à partir d'un cours ponctuel (jour + heure de début + durée en heures). */
export const punctualSessionRow = (date: any, startTime: any, durationHours: number): SessionRow => ({
    key: "punctual",
    date: date && typeof date.format === "function" ? date.format("YYYY-MM-DD") : moment(date).format("YYYY-MM-DD"),
    startTime: startTime ? moment(startTime).toISOString() : null,
    duration: durationHours,
});
